import React, { useState, useEffect, useMemo } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import Layout from "../components/Layout";
import PhasePersonType from "../components/onboarding/PhasePersonType";
import PhaseDocuments from "../components/onboarding/PhaseDocuments";
import PhaseDataReview from "../components/onboarding/PhaseDataReview";
import PhaseAddress from "../components/onboarding/PhaseAddress";
import PhaseContact from "../components/onboarding/PhaseContact";
import PhaseSummary from "../components/onboarding/PhaseSummary";
import PhaseCompanyData from "../components/onboarding/PhaseCompanyData";
import PhaseCompanyDocs from "../components/onboarding/PhaseCompanyDocs";
import PhaseRepresentative from "../components/onboarding/PhaseRepresentative";
import PhaseApoderado from "../components/onboarding/PhaseApoderado";
import PhaseBeneficialOwners from "../components/onboarding/PhaseBeneficialOwners";
import PhaseMoralSummary from "../components/onboarding/PhaseMoralSummary";

const API = process.env.REACT_APP_BACKEND_URL;

// Static PF (Persona Física) phases — left untouched per the
// regression contract. Tests against the legacy PF flow continue
// to navigate this exact sequence.
const PHASES_FISICA = [
  { id: "person_type", name: "Tipo de Persona", icon: "👤" },
  { id: "documents",   name: "Documentos",      icon: "📄" },
  { id: "data_review", name: "Revisión de Datos", icon: "✅" },
  { id: "address",     name: "Domicilio",       icon: "🏠" },
  { id: "contact",     name: "Contacto",        icon: "📧" },
  { id: "summary",     name: "Resumen",         icon: "📋" },
];

// Dynamic PM (Persona Moral) phases. The Apoderado step appears
// only when ``data.has_apoderado === true`` — branching happens in
// ``buildPhases()`` below so the progress bar reflects reality.
const PHASES_MORAL_BASE = [
  { id: "person_type",        name: "Tipo de Persona",      icon: "👤" },
  { id: "company_data",       name: "Empresa",              icon: "🏢" },
  { id: "company_docs",       name: "Documentos Empresa",   icon: "📄" },
  { id: "representative",     name: "Representante Legal",  icon: "🧑‍💼" },
  // apoderado slot is injected conditionally
  { id: "beneficial_owners",  name: "Beneficiarios",        icon: "👥" },
  { id: "summary",            name: "Resumen",              icon: "📋" },
];

function buildPhases(personType, hasApoderado) {
  if (personType !== "moral") return PHASES_FISICA;
  const phases = [...PHASES_MORAL_BASE];
  if (hasApoderado) {
    const repIdx = phases.findIndex((p) => p.id === "representative");
    phases.splice(repIdx + 1, 0, {
      id: "apoderado", name: "Apoderado Legal", icon: "🤝",
    });
  }
  return phases;
}


export default function NewCustomerPhased() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const resumeDraftId = searchParams.get("draft_id");
  const [currentPhase, setCurrentPhase] = useState(0);
  const [draftId, setDraftId] = useState(null);
  const [draftData, setDraftData] = useState({});
  const [ocrBySubject, setOcrBySubject] = useState({});
  const [loading, setLoading] = useState(false);
  const [isFirstSave, setIsFirstSave] = useState(true);
  const [resuming, setResuming] = useState(!!resumeDraftId);

  // PHASES is derived from the user's choice in step 1. We MUST recompute
  // on every render that follows a person_type / has_apoderado change so
  // the progress bar and phase dispatcher stay in sync.
  const PHASES = useMemo(
    () => buildPhases(draftData.person_type, !!draftData.has_apoderado),
    [draftData.person_type, draftData.has_apoderado],
  );

  // Resume a draft if `?draft_id=` is present in the URL. We refetch from
  // the server rather than trusting client state so the user can land here
  // from the Customers list and continue where they left off.
  useEffect(() => {
    if (!resumeDraftId) return;
    (async () => {
      try {
        const res = await axios.get(
          `${API}/api/customer-drafts/${resumeDraftId}`,
          { withCredentials: true },
        );
        const d = res.data || {};
        setDraftId(d.draft_id);
        setDraftData(d.merged_data || d.data || {});
        setOcrBySubject(d.ocr_by_subject || {});
        // Jump to the phase AFTER the last one completed so the user lands
        // on fresh work rather than re-confirming previous inputs.
        const completed = d.phases_completed || [];
        const phaseList = buildPhases(
          (d.data || {}).person_type,
          !!(d.data || {}).has_apoderado,
        );
        const nextIdx = phaseList.findIndex(
          (p) => !completed.includes(p.id),
        );
        setCurrentPhase(nextIdx === -1 ? phaseList.length - 1 : nextIdx);
        setIsFirstSave(false);
        toast.success("Borrador recuperado — continúa donde lo dejaste");
      } catch (err) {
        toast.error(
          err.response?.data?.detail ||
          "No se pudo recuperar el borrador",
        );
        navigate("/customers/new", { replace: true });
      } finally {
        setResuming(false);
      }
    })();
  }, [resumeDraftId, navigate]);

  // Auto-save draft
  const saveDraft = async (phase, data) => {
    try {
      // Safari cookie fix: Add delay before first save to ensure cookies propagate
      if (isFirstSave) {
        await new Promise(resolve => setTimeout(resolve, 500));
        setIsFirstSave(false);
      }

      const payload = {
        phase: PHASES[phase].id,
        data: { ...draftData, ...data }
      };

      if (!draftId) {
        // Create new draft
        const res = await axios.post(`${API}/api/customer-drafts/create`, payload, { withCredentials: true });
        setDraftId(res.data.draft_id);
        setDraftData(payload.data);
        toast.success("Progreso guardado");
      } else {
        // Update existing draft
        await axios.put(`${API}/api/customer-drafts/${draftId}/phase`, payload, { withCredentials: true });
        setDraftData(payload.data);
        toast.success("Progreso guardado");
      }

      return true;
    } catch (err) {
      toast.error("Falló al guardar el progreso");
      console.error(err);
      return false;
    }
  };

  const handleNext = async (phaseData) => {
    setLoading(true);
    const saved = await saveDraft(currentPhase, phaseData);
    setLoading(false);

    if (saved) {
      // Pull the freshest OCR-by-subject map from the server so the
      // next phase sees any extractions that finished while the user
      // was filling out the current step.
      if (draftId && draftData.person_type === "moral") {
        try {
          const res = await axios.get(
            `${API}/api/customer-drafts/${draftId}`,
            { withCredentials: true },
          );
          setOcrBySubject(res.data?.ocr_by_subject || {});
        } catch {
          // Non-fatal — phases can run without OCR pre-fill.
        }
      }
      if (currentPhase < PHASES.length - 1) {
        setCurrentPhase(currentPhase + 1);
        window.scrollTo(0, 0);
      }
    }
  };

  const handleBack = () => {
    if (currentPhase > 0) {
      setCurrentPhase(currentPhase - 1);
      window.scrollTo(0, 0);
    }
  };

  const handleFinalize = async () => {
    if (!draftId) {
      toast.error("No hay borrador para finalizar");
      return;
    }

    setLoading(true);
    try {
      const res = await axios.post(`${API}/api/customer-drafts/${draftId}/finalize`, {}, { withCredentials: true });
      toast.success("✅ Cliente creado exitosamente");
      const suffix = res.data.curp_download_started ? "?curpPending=1" : "";
      navigate(`/customers/${res.data.customer_id}${suffix}`);
    } catch (err) {
      // For PM finalize the summary phase wants to inspect the raw
      // error (e.g. 409 with blockers) and render a precise toast,
      // so we rethrow here instead of swallowing the message. For PF
      // the legacy toast is still emitted because the catch block
      // re-toasts on a generic detail.
      if (draftData.person_type !== "moral") {
        toast.error(err.response?.data?.detail || "Error al crear cliente");
      }
      throw err;
    } finally {
      setLoading(false);
    }
  };

  const renderPhase = () => {
    const props = {
      data: draftData,
      onNext: handleNext,
      onBack: handleBack,
      loading,
      draftId,
      ocrBySubject,
    };

    const id = PHASES[currentPhase]?.id;
    switch (id) {
      case "person_type":       return <PhasePersonType {...props} />;
      // PF legacy phases — unchanged routing.
      case "documents":         return <PhaseDocuments {...props} />;
      case "data_review":       return <PhaseDataReview {...props} />;
      case "address":           return <PhaseAddress {...props} />;
      case "contact":           return <PhaseContact {...props} />;
      case "summary":           return draftData.person_type === "moral"
        ? <PhaseMoralSummary {...props} onFinalize={handleFinalize} />
        : <PhaseSummary {...props} onFinalize={handleFinalize} />;
      // PM phases
      case "company_data":      return <PhaseCompanyData {...props} />;
      case "company_docs":      return <PhaseCompanyDocs {...props} />;
      case "representative":    return <PhaseRepresentative {...props} />;
      case "apoderado":         return <PhaseApoderado {...props} />;
      case "beneficial_owners": return <PhaseBeneficialOwners {...props} />;
      default:                  return null;
    }
  };

  return (
    <Layout>
      <div className="max-w-4xl mx-auto" data-testid="new-customer-phased-page">
        {resuming && (
          <div
            className="mb-6 p-4 bg-amber-500/10 border border-amber-500/30 rounded-lg text-amber-300 text-sm"
            data-testid="resuming-draft-banner"
          >
            Recuperando tu borrador…
          </div>
        )}
        {/* Progress Bar */}
        <div className="mb-8">
          <div className="flex justify-between items-center mb-4">
            <h1 className="text-2xl font-bold text-white">Nuevo Cliente - Onboarding KYC</h1>
            <span className="text-sm text-slate-400">Paso {currentPhase + 1} de {PHASES.length}</span>
          </div>

          <div className="flex items-center gap-2">
            {PHASES.map((phase, index) => (
              <React.Fragment key={phase.id}>
                <div className={`flex-1 flex flex-col items-center gap-2`}>
                  <div className={`w-10 h-10 rounded-full flex items-center justify-center text-lg transition-all ${
                    index < currentPhase ? "bg-teal-600 text-white" :
                    index === currentPhase ? "bg-teal-600 text-white ring-4 ring-teal-600/30" :
                    "bg-slate-800 text-slate-500"
                  }`}>
                    {index < currentPhase ? "✓" : phase.icon}
                  </div>
                  <span className={`text-xs hidden sm:block ${
                    index <= currentPhase ? "text-white" : "text-slate-500"
                  }`}>
                    {phase.name}
                  </span>
                </div>
                {index < PHASES.length - 1 && (
                  <div className={`flex-1 h-1 rounded ${
                    index < currentPhase ? "bg-teal-600" : "bg-slate-800"
                  }`} />
                )}
              </React.Fragment>
            ))}
          </div>
        </div>

        {/* Phase Content */}
        <div className="bg-slate-900 border border-slate-800 rounded-xl p-6">
          {renderPhase()}
        </div>
      </div>
    </Layout>
  );
}
